import React, { FC, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import TemplatesAdminPageBase, {
  TemplatesAdminPageProps,
} from './TemplatesAdminPageBase';
import { TemplateTypeConfigs } from './templateTypeConfigs';
import { DevelopmentPlanTemplateContent, TEMPLATE_TYPES } from './types';

const getTypeConfigs = (
  formatMessage: IntlShape['formatMessage']
): TemplateTypeConfigs => ({
  [TEMPLATE_TYPES.DEVELOPMENT_PLAN]: {
    emptyFormValues: { value: '', candidate_can_manage: false },
    fromContent: (content) => {
      const plan = content as DevelopmentPlanTemplateContent;
      return {
        value: plan.value ?? '',
        candidate_can_manage: !!plan.candidate_can_manage,
      };
    },
    inputs: [
      {
        name: 'value',
        type: INPUT_TYPES.RICH_TEXT_EDITOR,
        required: true,
        minRows: 8,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.development_plan_content',
          defaultMessage: 'Plan content',
        }),
      },
      {
        name: 'candidate_can_manage',
        type: INPUT_TYPES.SWITCH,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.candidate_can_manage',
          defaultMessage: 'Candidates can manage their own plans',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.candidate_can_manage_helper',
          defaultMessage:
            'The person a plan is for can edit it themselves, including while it is a draft. When off, only those above them in the chain of command, the plan supporter, and development plan admins can.',
        }),
      },
    ],
    validate: (obj) => {
      const errors: Record<string, string> = {};
      if (!(obj.value as string | undefined)?.trim()) {
        errors.value = formatMessage({
          id: 'app.views.administration.content_administration.templates.form.error.content_required',
          defaultMessage: 'Enter the plan content.',
        });
      }
      return errors;
    },
    toContent: (obj) => ({
      value: obj.value,
      candidate_can_manage: !!obj.candidate_can_manage,
    }),
    describeContent: (template) =>
      (template.content as DevelopmentPlanTemplateContent).candidate_can_manage
        ? formatMessage({
            id: 'app.views.administration.content_administration.templates.row.candidate_can_manage',
            defaultMessage: 'Candidates can create and manage their own plans',
          })
        : formatMessage({
            id: 'app.views.administration.content_administration.templates.row.manager_managed',
            defaultMessage: 'Managed by managers and development plan admins',
          }),
  },
});

const DevelopmentPlanTemplatesPage: FC<TemplatesAdminPageProps> = (props) => {
  const { formatMessage } = useIntl();
  const typeConfigs = useMemo(
    () => getTypeConfigs(formatMessage),
    [formatMessage]
  );
  return (
    <TemplatesAdminPageBase
      templateType={TEMPLATE_TYPES.DEVELOPMENT_PLAN}
      typeConfigs={typeConfigs}
      {...props}
    />
  );
};

export default React.memo(DevelopmentPlanTemplatesPage);
